import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { StatusDot } from '../components/StatusDot'
import { api, type EmailTemplate } from '../api/client'

// Canonical orderings used to sort per-title then per-purpose so new templates
// slot into the right section on creation without reorganizing the page.
const TITLE_ORDER = ['rc_car_action', 'flight_journal', 'model_airplane_news', 'diecast'] as const
const TITLE_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const PURPOSE_ORDER: Array<EmailTemplate['purpose']> = [
  'renewal', 'welcome', 'winback', 'marketing', 'transactional',
]
const PURPOSE_LABEL: Record<EmailTemplate['purpose'], string> = {
  renewal: 'Renewal',
  welcome: 'Welcome',
  winback: 'Win-back',
  marketing: 'Marketing',
  transactional: 'Transactional',
}
const PURPOSE_TONE: Record<EmailTemplate['purpose'], string> = {
  renewal: 'text-amber-glow',
  welcome: 'text-radar-green',
  winback: 'text-radar-violet',
  marketing: 'text-radar-cyan',
  transactional: 'text-paper',
}

// Matches the autoincrement ids seeded in aamcirc/db/seed.py.
const PUB_SLUG_BY_ID: Record<number, string> = {
  1: 'rc_car_action',
  2: 'model_airplane_news',
  3: 'flight_journal',
  4: 'diecast',
}

type TitleGroup = {
  key: string                           // publication slug or '__multi__'
  title: string                         // display name
  byPurpose: Map<EmailTemplate['purpose'], EmailTemplate[]>
  totalCount: number
}

function groupTemplates(templates: EmailTemplate[]): TitleGroup[] {
  const bySlug: Map<string, EmailTemplate[]> = new Map()
  for (const t of templates) {
    const slug = (t.publication_id != null && PUB_SLUG_BY_ID[t.publication_id])
      || '__multi__'
    const bucket = bySlug.get(slug) ?? []
    bucket.push(t)
    bySlug.set(slug, bucket)
  }

  const groups: TitleGroup[] = []
  for (const slug of TITLE_ORDER) {
    const items = bySlug.get(slug)
    if (!items?.length) continue
    groups.push(makeGroup(slug, items))
  }
  if (bySlug.has('__multi__')) {
    groups.push(makeGroup('__multi__', bySlug.get('__multi__')!))
  }
  return groups
}

function makeGroup(slug: string, templates: EmailTemplate[]): TitleGroup {
  const byPurpose = new Map<EmailTemplate['purpose'], EmailTemplate[]>()
  for (const t of templates) {
    const arr = byPurpose.get(t.purpose) ?? []
    arr.push(t)
    byPurpose.set(t.purpose, arr)
  }
  for (const arr of byPurpose.values()) {
    arr.sort((a, b) => a.slug.localeCompare(b.slug))
  }
  return {
    key: slug,
    title: slug === '__multi__' ? 'Cross-title / generic' : (TITLE_NAMES[slug] ?? slug),
    byPurpose,
    totalCount: templates.length,
  }
}

export function TemplatesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ items: EmailTemplate[] }>('/api/templates'),
  })
  const groups = data ? groupTemplates(data.items) : []

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Composition · email</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Templates
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">HTML library</span>
          </h1>
        </div>
        <Link to="/templates/new">
          <Button size="lg">+ New Template</Button>
        </Link>
      </header>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
      {!isLoading && data && data.items.length === 0 && (
        <div className="label text-paper-trace p-6">
          No templates yet — create your first with <span className="label-amber">+ New Template</span>.
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => (
          <TitleSection key={g.key} group={g} />
        ))}
      </div>
    </div>
  )
}

function TitleSection({ group }: { group: TitleGroup }) {
  const purposes: EmailTemplate['purpose'][] = PURPOSE_ORDER.filter(
    (p) => (group.byPurpose.get(p)?.length ?? 0) > 0,
  )
  const isMulti = group.key === '__multi__'

  return (
    <details open className="group/section border border-ink-500 bg-ink-800/40">
      <summary className="flex items-baseline justify-between px-4 py-3 cursor-pointer list-none select-none border-b border-ink-500/60 hover:bg-ink-700/30">
        <span className="flex items-center gap-3">
          {isMulti ? (
            <span className="label-amber">{group.title}</span>
          ) : (
            <PubBadge slug={group.key} />
          )}
          <span className="label text-paper-trace">
            {group.totalCount} template{group.totalCount === 1 ? '' : 's'}
          </span>
        </span>
        <span className="label text-paper-trace group-open/section:rotate-90 transition-transform">
          ▸
        </span>
      </summary>

      <div>
        {purposes.map((purpose) => (
          <PurposeBlock
            key={purpose}
            purpose={purpose}
            templates={group.byPurpose.get(purpose) ?? []}
          />
        ))}
      </div>
    </details>
  )
}

function PurposeBlock({
  purpose,
  templates,
}: {
  purpose: EmailTemplate['purpose']
  templates: EmailTemplate[]
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const clone = useMutation({
    mutationFn: (id: number) => api.post<EmailTemplate>(`/api/templates/${id}/clone`, {}),
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      navigate(`/templates/${t.id}`)
    },
  })
  return (
    <section>
      <div className="flex items-baseline justify-between bg-ink-700/40 px-4 py-1.5 border-b border-ink-500/40">
        <span className={`label-amber ${PURPOSE_TONE[purpose]}`}>
          {PURPOSE_LABEL[purpose]}
        </span>
        <span className="label text-paper-trace">
          {templates.length} template{templates.length === 1 ? '' : 's'}
        </span>
      </div>

      <table className="w-full text-[13px] font-mono">
        <tbody>
          {templates.map((t) => (
            <tr
              key={t.id}
              className={`border-b border-ink-500/30 hover:bg-ink-700/20 ${t.enabled ? '' : 'opacity-60'}`}
            >
              <td className="px-4 py-2 w-[240px]">
                <Link to={`/templates/${t.id}`} className="text-paper hover:text-amber-glow">
                  {t.slug}
                </Link>
              </td>
              <td className="px-3 py-2 text-paper-dim">{t.name}</td>
              <td className="px-3 py-2 text-paper-dim truncate max-w-[320px]">
                <span className="font-mono text-[11px] text-paper-trace">{t.subject}</span>
              </td>
              <td className="px-3 py-2 w-[80px] text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  title="Copy this template under a new slug"
                  disabled={clone.isPending}
                  onClick={() => clone.mutate(t.id)}
                >
                  Clone
                </Button>
              </td>
              <td className="px-3 py-2 w-[110px]">
                <span className="flex items-center gap-2">
                  <StatusDot tone={t.enabled ? 'ok' : 'muted'} pulse={t.enabled} />
                  <span className="label">{t.enabled ? 'ENABLED' : 'DISABLED'}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
